import { FirestoreDataConverter, Timestamp } from 'firebase/firestore';
import { getMonthDay, getWeekDayEnum, isSpecialDay, removeNullFields } from '../util/util';
import { Entity } from './entity';
import { HabitFrequencyType, HabitTarget, SpecialDays, WeekDay } from './habit.enum';

export interface HabitFrequency {
  type: HabitFrequencyType;
  count: number;
  weekDays?: WeekDay[];
  monthDays?: number[];
  specialDays?: SpecialDays;
}

export interface Habit extends Entity {
  title: string;
  notes?: string;
  start: Date;
  end: Date | null;
  target: HabitTarget;
  frequency: HabitFrequency;
}

export interface FirestoreHabit extends Omit<Habit, 'start' | 'end'> {
  start: Timestamp;
  end: Timestamp | null;
}

export const HabitConverter: FirestoreDataConverter<Habit, FirestoreHabit> = {
  toFirestore: (habit: Habit): FirestoreHabit => {
    return {
      ...removeNullFields({ ...habit }),
      start: Timestamp.fromDate(habit.start),
      end: habit.end ? Timestamp.fromDate(habit.end) : null,
    };
  },
  fromFirestore: (snapshot, options): Habit => {
    const data = snapshot.data(options) as FirestoreHabit;
    return {
      ...data,
      start: data.start.toDate(),
      end: data.end?.toDate() ?? null,
    };
  },
};

export function isCountTask(habit: Habit): boolean {
  return [HabitFrequencyType.WEEKLY, HabitFrequencyType.MONTHLY].includes(habit.frequency.type);
}

export function isDayTask(habit: Habit, date: Date): boolean {
  switch (habit.frequency.type) {
    case HabitFrequencyType.DAILY:
    case HabitFrequencyType.WEEKLY:
    case HabitFrequencyType.MONTHLY:
      return true;

    case HabitFrequencyType.WEEK_DAYS:
      return habit.frequency.weekDays?.includes(getWeekDayEnum(date)) ?? false;

    case HabitFrequencyType.MONTH_DAYS:
      return habit.frequency.monthDays?.includes(getMonthDay(date)) ?? false;

    case HabitFrequencyType.SPECIAL_DAYS:
      return habit.frequency.specialDays ? isSpecialDay(habit.frequency.specialDays, date) : false;
  }
}

export const HabitCollection = 'habits';
